<template>
  <esg-base-chart max-width="768px">
    <loading :active="loading"
             :z-index="1000"
             :is-full-page="false"/>
    <div :class="{ hidden: loading }"
         class="d-flex align-items-center justify-content-around">
      <div v-show="!loading"
           class="d-none d-md-flex flex-column align-items-center justify-content-center w-25">
        <div class="label">연간 배출량</div>
        <div class="emission">{{ formatNumber(total, 0) }}</div>
        <div class="unit">tCO2e</div>
      </div>
      <div class="w-75">
        <apex-chart type="bar"
                    ref="chart"
                    :width=this.width
                    :height=this.height
                    :options="chartOptions"
                    :series="series"/>
      </div>
    </div>
  </esg-base-chart>
</template>
<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";

export default {
  components: { EsgBaseChart },
  props: {
    scope: {
      type: Number,
      required: true,
    },
    color: {
      type: String,
      required: true,
    },
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '250px',
    },
  },
  data() {
    return {
      loading: false,
      total: 0,
      series: [],
      chartOptions: {
        colors: [this.color],
        chart: {
          fontFamily: 'NanumGothic, serif',
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            dataLabels: {
              position: 'top'
            }
          },
        },
        xaxis: { //x 축
          categories: [],
          crosshairs: { show: false }
        },
        dataLabels: {
          enabled: true,
          formatter: val => this.formatNumber(val, 0),
          style: {
            fontWeight: 'normal',
            fontFamily: 'NanumGothic',
            colors: ['#000']
          },
          offsetY: -20
        },
        yaxis: {
          show: true,
          title: {
            text: '탄소 배출량 (tCO2)',
            style: {
              fontSize: 12,
              fontWeight: 'normal',
            }
          },
          labels: {
            formatter: val => this.compactFormatNumber(val)
          }
        },
        tooltip: {
          y: {
            formatter: val => this.formatNumber(val) + " tCO2"
          },
          custom: ({ series, seriesIndex, dataPointIndex }) => {
            if (dataPointIndex === 0) return null;

            const curr = series[seriesIndex][dataPointIndex];
            const prev = series[seriesIndex][dataPointIndex - 1];
            const diff = 100 * (curr - prev) / prev;
            const arrow = diff >= 0 ? '▲' : '▼';
            const abs = this.formatNumber(Math.abs(diff), 2);

            return `
              <div class="apexcharts-tooltip-box">
                <span>이전 분기 대비 ${arrow} ${abs} %</span>
              </div>
            `
          }
        },
        grid: {
          show: false
        },
        legend: {
          position: 'right',
        },
      },
    }
  },
  created() {
    this.fetchData();
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;

        let year = new Date().getFullYear();
        let years = [year];
        let scopes = [this.scope];
        let args = [{
          years,
          scopes,
          groupKeys: ['year', 'quarter']
        }];

        let data = new FormData()
        data.append('path', 'esg/ghg_emission/main');
        data.append('json', JSON.stringify(args));

        this.abortController = new AbortController();
        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        if (!response.data.data) {
          this.series = [];
          return;
        }

        let list = JSON.parse(response.data.data)[0].data;
        let quarters = [1, 2, 3, 4];

        this.setTotal(list);
        this.setSeries(list, quarters);
        this.setLabels(year, quarters);
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
        this.refreshChart();
      }
    },
    setTotal(list) {
      this.total = list.map(row => row.values).reduce((a, b) => a + b, 0)
    },
    setSeries(list, quarters) {
      let data = quarters.map(quarter => {
        let row = list.filter(row => row.quarter === quarter).pop()
        return row ? row.values : 0;
      });
      this.series.push({
        name: 'Scope ' + this.scope,
        data
      });
    },
    setLabels(year, quarters) {
      this.chartOptions.xaxis.categories = quarters.map(quarter => `${year}년 ${quarter}Q`);
    },
  }
}
</script>

<style scoped>
.total {
  font-family: NanumGothic, serif;
  color: black;
}

.label {
  font-size: 1.25rem;
  font-weight: 500;
}

.emission {
  font-size: 1.5rem;
  font-weight: 600;
}

.unit {
  font-size: 1rem;
  color: #555;
}

.hidden {
  display: none !important;
}
</style>
